import { useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { MeterSizeType, SubmitMeterSizeType } from "@/types/meter"
import { ConnectionType } from "@/types/zone-connection-rates-type"
import { addMeterSize, editMeterSize, getConnectionType } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ComboboxSearch } from "@/components/combobox-search"
import { Icons } from "@/components/icons"

const formSchema = z.object({
  connection_type_id: z.number().min(1, "Please select a connection type"),
  size: z.string().min(1, "Please select size"),
  description: z.string(),
})

export function MeterSizeForm({
  setShowMeterSizeModal,
  onClose,
  editData,
}: {
  setShowMeterSizeModal: React.Dispatch<React.SetStateAction<boolean>>
  onClose: () => void
  editData?: MeterSizeType
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["connection_type"],
    queryFn: getConnectionType, // ✅ Calls the function properly
  })
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id } = useParams({ strict: false })
  const numericId = id ? Number(id) : undefined

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: editData
      ? {
          connection_type_id: editData.connection_type_id,
          size: editData.size,
          description: editData.description,
        }
      : {
          connection_type_id: 0,
          size: "",
          description: "",
        },
  })

  useEffect(() => {
    if (editData) {
      form.reset({
        connection_type_id: editData.connection_type_id,
        size: editData.size ?? "",
        description: editData.description ?? "",
      })
    }
  }, [editData, form])

  const connectionTypes: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (data as ConnectionType[]) // Ensure data is an array
      ?.map((item) => ({
        value: item.type_name,
        label: item.type_name,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id?: number
      payload: SubmitMeterSizeType
    }) => {
      if (id) {
        await editMeterSize(payload, id)
      } else {
        await addMeterSize(payload)
      }
    },
    onSuccess: () => {
      toast({
        description: editData
          ? "Edited Successfully"
          : "Added Meter Size Successfully",
      })
      queryClient.invalidateQueries({ queryKey: ["metersize"] })
      onClose()
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    const payload: SubmitMeterSizeType = {
      connection_type_id: values.connection_type_id,
      size: String(values.size),
      description: values.description,
    }

    mutation.mutate(editData ? { payload, id: editData?.id } : { payload })
  }
  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />

      <Form {...form}>
        <form
          className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Card className="my-4 grid-cols-2 gap-4 p-6 md:grid">
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="connection_type_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="py-1">
                      Connection type
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <ComboboxSearch
                        field={{
                          ...field,
                        }}
                        options={connectionTypes}
                        placeholder="Select a connection type"
                        idAsValue
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="py-1">
                      Size <span className="text-destructive">*</span>
                    </FormLabel>
                    <Input type="number" placeholder="Input size" {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="col-start-1 col-end-3 mb-4 space-y-1.5">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <label htmlFor="textarea">Description</label>
                    <FormControl>
                      <Textarea id="textarea" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>

          <div className="col-span-2 flex w-full justify-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowMeterSizeModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            {editData ? (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            ) : (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Add
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
